import { useEffect, useRef } from "react";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { asList, asRecord, hasValue } from "./AllMattersV2Fields";
import {
  DataTable,
  KeyValues,
  PropertyTypeTag,
  RankBadge,
  Section,
  Tag,
  erasedTextSx,
  formatDate,
  rightTone,
  text,
  type Tone,
} from "./AllMattersV2Parts";

const halfWidth = (value: unknown): string =>
  text(value).replace(/[０-９]/g, (digit) =>
    String.fromCharCode(digit.charCodeAt(0) - 0xfee0)
  );

/** `(ぬ)第6236号`; old catalogs without a kana mark keep their source number. */
export const jointCatalogNumber = (catalog: unknown): string => {
  const item = asRecord(catalog);
  return item.記号
    ? `(${text(item.記号)})第${text(item.番号)}号`
    : text(item.番号原文) || `第${text(item.番号) || "—"}号`;
};

/**
 * Index of the catalog a 乙区 reference (`{記号, 番号}`) points at, or -1.
 * The mark and the number together identify a catalog (規則166条1項).
 */
export const findJointCatalog = (
  catalogs: unknown[],
  reference: unknown
): number => {
  const ref = asRecord(reference);
  if (!hasValue(ref.番号)) return -1;
  return catalogs.findIndex((catalog) => {
    const item = asRecord(catalog);
    return (
      text(item.記号) === text(ref.記号) &&
      halfWidth(item.番号) === halfWidth(ref.番号)
    );
  });
};

// Events that take a row out of the catalog, as spurs does for current rows.
const removingEvents = new Set(["抹消", "全部抹消", "滅失", "合筆"]);

/** A row is out of the catalog when underlined or erased by a 予備 event. */
export const jointItemRemoved = (item: unknown): boolean => {
  const row = asRecord(item);
  return (
    row.抹消推定 === true ||
    asList(row.予備).some((event) =>
      removingEvents.has(text(asRecord(event).種別))
    )
  );
};

const eventTones: Record<string, Tone> = {
  追加: "success",
  変更: "secondary",
  職権更正: "secondary",
  分筆: "secondary",
  換地処分: "secondary",
  移記: "info",
};

/** 予備 events: kind, date and receipt number first, then the source text. */
const Events = ({ events }: { events: unknown[] }): JSX.Element | null =>
  events.length ? (
    <Stack spacing={0.5}>
      {events.map((event, index) => {
        const item = asRecord(event);
        const kind = text(item.種別);
        const receipt = [
          formatDate(item.日付),
          item.受付番号 ? `第${halfWidth(item.受付番号)}号` : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <Stack
            key={index}
            direction="row"
            gap={1}
            alignItems="baseline"
            flexWrap="wrap"
          >
            {kind && kind !== "その他" && (
              <Tag
                label={kind}
                tone={eventTones[kind] ?? "default"}
                filled={removingEvents.has(kind)}
              />
            )}
            {receipt && (
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                {receipt}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {typeof event === "string" ? event : text(item.原文)}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  ) : null;

/**
 * Kind chips of a row. Schema 6.2 names the property (`物件種別`) and flags a
 * share (`持分対象`); older responses and an unknown property keep `種別`.
 */
export const jointItemKinds = (item: unknown): string[] => {
  const row = asRecord(item);
  const property = text(row.物件種別);
  if (property && property !== "不明")
    return row.持分対象 === true ? [property, "持分"] : [property];
  const kind = text(row.種別);
  return kind && kind !== "不明" ? [kind] : [];
};

const ItemKindTags = ({ item }: { item: unknown }): JSX.Element => (
  <>
    {jointItemKinds(item).map((kind) =>
      kind === "持分" ? (
        <Tag key={kind} label="持分" tone="secondary" />
      ) : (
        <PropertyTypeTag key={kind} type={kind} />
      )
    )}
  </>
);

/**
 * `乙山花子持分一部（順位3番で取得）`, or `持分2分の1` without a person.
 * The share is never widened to the whole.
 */
export const shareLabel = (value: unknown): string => {
  const item = asRecord(value);
  const rank = asRecord(item.取得順位参照);
  return [
    `${text(item.対象人物)}持分`,
    item.持分一部 === true ? "一部" : "",
    hasValue(item.持分) ? text(item.持分) : "",
    hasValue(rank)
      ? `（順位${halfWidth(rank.原文 || rank.順位)}番で取得）`
      : "",
  ].join("");
};

/** One line per right, for compact lists such as the overview. */
export const rightSummary = (value: unknown): string => {
  const item = asRecord(value);
  const kind = text(item.種類);
  if (kind === "敷地権")
    return [
      `敷地権 符号${text(item.敷地符号)}`,
      text(item.土地表示),
      text(item.敷地権種類),
      hasValue(item.敷地権割合) ? `割合 ${text(item.敷地権割合)}` : "",
    ]
      .filter(Boolean)
      .join("　");
  if (kind === "持分") return shareLabel(item);
  // 土地・建物 cover the property itself, which the row already names.
  if (kind === "土地" || kind === "建物") return "";
  return text(item.原文) || (kind !== "不明" ? kind : "");
};

/** Site rights and shares in tables; anything unparsed keeps its source text. */
const TargetRights = ({
  rights,
}: {
  rights: unknown[];
}): JSX.Element | null => {
  const kind = (right: unknown): string => text(asRecord(right).種類);
  const sites = rights.filter((right) => kind(right) === "敷地権");
  const shares = rights.filter((right) => kind(right) === "持分");
  const others = rights.filter(
    (right) => !["敷地権", "持分", "土地", "建物"].includes(kind(right))
  );
  if (!sites.length && !shares.length && !others.length) return null;
  return (
    <Stack spacing={1}>
      {sites.length > 0 && (
        <DataTable
          columns={[
            { label: "敷地権 符号", width: "5.5em" },
            { label: "土地の表示" },
            { label: "種類", width: "4.5em" },
            { label: "割合", width: "7em" },
          ]}
          rows={sites.map((right, index) => {
            const item = asRecord(right);
            return {
              key: index,
              cells: [
                text(item.敷地符号),
                text(item.土地表示) || text(item.原文),
                text(item.敷地権種類),
                text(item.敷地権割合),
              ],
            };
          })}
        />
      )}
      {shares.map((right, index) => (
        <Stack
          key={index}
          direction="row"
          gap={1}
          alignItems="baseline"
          flexWrap="wrap"
        >
          <Tag label="持分" tone="secondary" />
          <Typography variant="body2">{shareLabel(right)}</Typography>
        </Stack>
      ))}
      {others.map((right, index) => {
        // 地上権・根抵当権・転抵当 and the like: the right's name, then its text.
        const name = kind(right) === "不明" ? "" : kind(right);
        const source = text(asRecord(right).原文);
        return (
          <Stack
            key={index}
            direction="row"
            gap={1}
            alignItems="baseline"
            flexWrap="wrap"
          >
            {name && <Tag label={name} tone={rightTone(name)} />}
            {source !== name && (
              <Typography variant="body2">{source}</Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

/** Rank with its same-rank mark and appendix; another office replaces it. */
const ItemRank = ({ item }: { item: Record<string, unknown> }): JSX.Element => {
  const rank = asRecord(item.順位番号);
  if (hasValue(item.管轄登記所)) return <Tag label="他管轄" tone="warning" />;
  return hasValue(rank.原文) ? (
    <RankBadge
      label={`順位 ${text(rank.原文)}`}
      appendix={rank.付記番号 != null}
    />
  ) : (
    <Typography variant="body2" color="text.secondary">
      順位番号なし
    </Typography>
  );
};

const JointItem = ({
  item,
  catalogErased,
}: {
  item: unknown;
  catalogErased: boolean;
}): JSX.Element => {
  const row = asRecord(item);
  const removed = catalogErased || jointItemRemoved(row);
  return (
    <Paper
      variant="outlined"
      component="article"
      aria-label={`明細 ${text(row.番号) || "番号なし"}`}
      sx={(theme) => ({
        p: 1.5,
        borderLeft: 4,
        borderLeftColor: removed
          ? theme.palette.text.disabled
          : theme.palette.info.main,
        bgcolor: removed ? "action.hover" : undefined,
      })}
    >
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" fontWeight={700}>
          {row.番号 != null ? `明細 ${text(row.番号)}` : "明細"}
        </Typography>
        <ItemKindTags item={row} />
        {row.抹消推定 === true && <Tag label="抹消推定" filled />}
        <Box sx={{ ml: "auto" }}>
          <ItemRank item={row} />
        </Box>
      </Stack>
      <Stack
        spacing={1}
        mt={1}
        sx={removed ? { "& p, & td": erasedTextSx } : undefined}
      >
        <KeyValues
          items={[
            ["所在", text(row.所在)],
            ["地番", text(row.地番)],
            ["家屋番号", text(row.家屋番号)],
            ["管轄登記所", text(row.管轄登記所)],
            [
              "表示",
              hasValue(row.所在) || hasValue(row.地番) || hasValue(row.家屋番号)
                ? ""
                : text(row.表示原文),
            ],
          ]}
        />
        <TargetRights rights={asList(row.対象権利)} />
        <Events events={asList(row.予備)} />
      </Stack>
    </Paper>
  );
};

const catalogDomId = (index: number): string => `v2-joint-catalog-${index}`;

export const JointSecurityCatalogs = ({
  catalogs,
  showErased,
  focusIndex,
  onFocused,
  stickyHeight,
}: {
  catalogs: unknown[];
  showErased: boolean;
  /** A catalog opened from a reference; scrolled to and focused once. */
  focusIndex?: number | null;
  onFocused?: () => void;
  /** Height of the sticky section tabs, which wrap on phones. */
  stickyHeight?: () => number;
}): JSX.Element => {
  const refs = useRef<Record<number, HTMLElement | null>>({});
  useEffect(() => {
    if (focusIndex == null) return;
    const target = refs.current[focusIndex];
    if (target) {
      // Keep the heading clear of the sticky section tabs.
      target.style.scrollMarginTop = `${(stickyHeight?.() ?? 0) + 16}px`;
      target.scrollIntoView?.({ block: "start" });
      target.focus({ preventScroll: true });
    }
    onFocused?.();
  }, [focusIndex, onFocused, stickyHeight]);
  const hiddenCatalogs = showErased
    ? 0
    : catalogs.filter((catalog) => asRecord(catalog).全部抹消済み === true)
        .length;
  return (
    <Stack spacing={3}>
      {hiddenCatalogs > 0 && (
        <Typography variant="body2" color="text.secondary">
          全部抹消済みの目録 {hiddenCatalogs}件を非表示
        </Typography>
      )}
      {hiddenCatalogs === catalogs.length && (
        <Alert severity="info">
          抹消されていない共同担保目録はありません。
        </Alert>
      )}
      {catalogs.map((catalog, index) => {
        const item = asRecord(catalog);
        const wholeErased = item.全部抹消済み === true;
        if (wholeErased && !showErased) return null;
        const rows = asList(item.明細);
        const visible = rows.filter(
          (row) => showErased || !jointItemRemoved(row)
        );
        const catalogEvents = asList(item.予備);
        const moved = catalogEvents.some(
          (event) => text(asRecord(event).種別) === "移記"
        );
        return (
          <Box
            key={index}
            id={catalogDomId(index)}
            ref={(element: HTMLElement | null) => {
              refs.current[index] = element;
            }}
            tabIndex={-1}
            sx={{ outline: "none" }}
          >
            <Section
              title={`共同担保目録 ${jointCatalogNumber(item)}`}
              tone={wholeErased ? "default" : "info"}
              tags={
                <>
                  {wholeErased && <Tag label="全部抹消済み" filled />}
                  {moved && <Tag label="移記" tone="info" />}
                  {hasValue(item.調製) && (
                    <Typography variant="caption" color="text.secondary">
                      調製 {formatDate(item.調製)}
                    </Typography>
                  )}
                  {visible.length < rows.length && (
                    <Typography variant="caption" color="text.secondary">
                      抹消された明細 {rows.length - visible.length}件を非表示
                    </Typography>
                  )}
                </>
              }
            >
              {catalogEvents.length > 0 && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    mb={0.5}
                  >
                    目録の予備
                  </Typography>
                  <Events events={catalogEvents} />
                </Box>
              )}
              {visible.length > 0 ? (
                visible.map((row, rowIndex) => (
                  <JointItem
                    key={rowIndex}
                    item={row}
                    catalogErased={wholeErased}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  表示する明細はありません。
                </Typography>
              )}
            </Section>
          </Box>
        );
      })}
    </Stack>
  );
};

/** A 乙区 reference chip; opens its catalog when the response has it. */
export const JointCatalogChip = ({
  reference,
  onOpen,
}: {
  reference: unknown;
  onOpen?: () => void;
}): JSX.Element => (
  <Tag
    label={
      onOpen ? (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {text(reference)}
          <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </Box>
      ) : (
        text(reference)
      )
    }
    tone="info"
    onClick={onOpen}
    ariaLabel={onOpen ? `共同担保目録 ${text(reference)} を開く` : undefined}
  />
);

const currentValue = (value: unknown): unknown =>
  "値" in asRecord(value) ? asRecord(value).値 : value;

/**
 * Half-width reference, lot and house number of a current row (Schema 6.2
 * `半角表記`); older rows keep the full-width copies of the source text.
 */
const currentNumber = (
  item: Record<string, unknown>,
  key: "目録参照" | "地番" | "家屋番号"
): string =>
  text(asRecord(item.半角表記)[key]) || text(currentValue(item[key]));

/**
 * Kind chips of a current row. Schema 6.2 copies 物件種別 and 持分対象 onto
 * the row; older rows read them (or 種別) from the catalog row they point at.
 */
const currentItemKinds = (
  item: Record<string, unknown>,
  source: unknown
): string[] =>
  jointItemKinds(
    "物件種別" in item
      ? {
          物件種別: item.物件種別,
          持分対象: item.持分対象,
          種別: asRecord(source).種別,
        }
      : source
  );

/**
 * Current joint-security rows of a security in the overview, grouped by
 * catalog: kind, location with lot or house number, and the target rights.
 */
export const CurrentJointSecurities = ({
  items,
  catalogs,
  onOpenCatalog,
}: {
  items: unknown[];
  catalogs: unknown[];
  onOpenCatalog?: (index: number) => void;
}): JSX.Element => {
  const groups = new Map<string, unknown[]>();
  items.forEach((entry) => {
    const key = String(
      asRecord(entry).目録Index ?? currentNumber(asRecord(entry), "目録参照")
    );
    groups.set(key, [...(groups.get(key) ?? []), entry]);
  });
  return (
    <Stack spacing={1}>
      {[...groups.values()].map((group, groupIndex) => {
        const first = asRecord(group[0]);
        const catalogIndex =
          typeof first.目録Index === "number" ? first.目録Index : -1;
        const label =
          currentNumber(first, "目録参照") ||
          (catalogIndex >= 0
            ? `目録${jointCatalogNumber(catalogs[catalogIndex])}`
            : "");
        return (
          <Stack key={groupIndex} spacing={0.5}>
            {label && (
              <Box>
                <JointCatalogChip
                  reference={label}
                  onOpen={
                    onOpenCatalog && catalogs[catalogIndex] !== undefined
                      ? () => {
                          onOpenCatalog(catalogIndex);
                        }
                      : undefined
                  }
                />
              </Box>
            )}
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {group.map((entry, index) => {
                const item = asRecord(entry);
                const source = asRecord(
                  asList(asRecord(catalogs[catalogIndex]).明細)[
                    typeof item.明細Index === "number" ? item.明細Index : -1
                  ]
                );
                const house = currentNumber(item, "家屋番号");
                const place = [
                  text(currentValue(item.所在)),
                  currentNumber(item, "地番"),
                  house ? `家屋番号 ${house}` : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                const rights = asList(currentValue(item.対象権利))
                  .map(rightSummary)
                  .filter(Boolean);
                return (
                  <Box component="li" key={index}>
                    <Stack
                      direction="row"
                      gap={0.5}
                      alignItems="baseline"
                      flexWrap="wrap"
                    >
                      {currentItemKinds(item, source).map((kind) => (
                        <Tag key={kind} label={kind} tone="default" />
                      ))}
                      <Typography variant="body2" component="span">
                        {place || text(currentValue(item.現在表示))}
                      </Typography>
                    </Stack>
                    {rights.map((right) => (
                      <Typography
                        key={right}
                        variant="body2"
                        color="text.secondary"
                      >
                        {right}
                      </Typography>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};
